'use client';

import { useState } from 'react';
import Image from 'next/image';
import { toMediaProxyPath } from '@/components/toMediaProxyPath';

interface ImageData {
  url: string;
  name?: string;
  alternativeText?: string;
  width?: number;
  height?: number;
}

interface ImageGalleryProps {
  images: ImageData[];
  alt: string;
  maxThumbnails?: number;
}

export default function ImageGallery({ images, alt, maxThumbnails = 3 }: ImageGalleryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  // Sort images alphabetically by name
  const sortedImages = [...images].sort((a, b) => {
    const nameA = a.name || a.url;
    const nameB = b.name || b.url;
    return nameA.localeCompare(nameB);
  });

  const displayImages = sortedImages.slice(0, maxThumbnails);
  const remainingCount = sortedImages.length - maxThumbnails;

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeLightbox = () => {
    setIsOpen(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? sortedImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
  };

  return (
    <>
      {/* Thumbnail Stack - Full Width */}
      <div className="space-y-2 mb-2">
        {displayImages.map((img, idx) => (
          <div
            key={idx}
            className="relative w-full h-32 cursor-pointer overflow-hidden rounded-lg border hover:opacity-80 transition-opacity"
            style={{ borderColor: 'var(--border-primary)' }}
            onClick={() => openLightbox(idx)}
          >
            <Image
              src={toMediaProxyPath(img.url) || '/placeholder.png'}
              alt={img.alternativeText || `${alt} ${idx + 1}`}
              fill
              className="object-contain"
              sizes="(max-width: 768px) 100vw, 700px"
            />
            {/* "+N more" overlay on last thumbnail if there are more images */}
            {idx === maxThumbnails - 1 && remainingCount > 0 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="text-white text-lg font-semibold">
                  +{remainingCount} more
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-3xl hover:opacity-70 transition-opacity z-10"
            aria-label="Close gallery"
          >
            ✕
          </button>

          {/* Previous button */}
          {sortedImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 text-white text-4xl hover:opacity-70 transition-opacity z-10"
              aria-label="Previous image"
            >
              ‹
            </button>
          )}

          {/* Image container */}
          <div
            className="relative max-w-5xl max-h-[80vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-full">
              <Image
                src={toMediaProxyPath(sortedImages[currentIndex].url) || '/placeholder.png'}
                alt={sortedImages[currentIndex].alternativeText || `${alt} ${currentIndex + 1}`}
                fill
                className="object-contain"
                sizes="(max-width: 1200px) 100vw, 1200px"
                priority
              />
            </div>
          </div>

          {/* Next button */}
          {sortedImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 text-white text-4xl hover:opacity-70 transition-opacity z-10"
              aria-label="Next image"
            >
              ›
            </button>
          )}

          {/* Image counter and dots */}
          {sortedImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white z-10">
              <div className="flex items-center gap-2 mb-2">
                {sortedImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentIndex
                        ? 'bg-white w-8'
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
              <div className="text-center text-sm">
                {currentIndex + 1} / {sortedImages.length}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
